import { IfWrites } from '../../components/write-access';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { SubjectDataClaim, SubjectDataLineage, SubjectDataUnattachedClaim } from '@ni/shared';
import { usePersonRecord } from './record';
import { useOrg } from '../../api/app-context';
import { useSubjectData } from '../../api/hooks';
import { loadErrorLine } from '../../api/errors';
import { FACT_TYPE_LABELS, NEGATION_LABELS, sourceTierLabel } from '../../lib/copy';
import { formatDate, formatDay, joinClauses, plural } from '../../lib/format';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import { Section } from '../../components/ui/section';

/**
 * The Data tab: the substrate behind the brief, read-only. It shows what the
 * composed reads fold away — superseded versions, every claim's provenance, and
 * the claims behind no fact at all.
 *
 * The one surface where the machine's own vocabulary is allowed through: this is
 * the power view, and paraphrasing an enum here would be inventing words.
 * Corrections stay on Research, which every lineage links across to where the
 * record has one — an organisation's does not, and says so by leaving the line
 * off.
 */

/** Short form of a uuid — enough to match two rows by eye, not a value to read out. */
const shortId = (id: string): string => id.slice(0, 8);

/**
 * A version's `fields`, rendered flat. Entity references carry a name and an
 * id; everything else is whatever the catalog's schema for that type says, so
 * this stays structural rather than pretending to know the shape.
 */
function fieldsLine(fields: Record<string, unknown> | null): string {
  if (!fields) return 'no payload';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(fields)) {
    const rendered = renderValue(value);
    // The catalog's own field names, uncapitalised: this view shows the
    // machine's vocabulary rather than a plain-language rewrite of it.
    if (rendered.length > 0) parts.push(`${key} ${rendered}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'no fields';
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(renderValue).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const name = record['name'];
    if (typeof name === 'string') return name;
    return Object.values(record).map(renderValue).filter(Boolean).join(' ');
  }
  return String(value);
}

/* ————————————————————————— claims ————————————————————————— */

function ClaimsTable({
  claims,
  withType = false,
}: {
  claims: (SubjectDataClaim | SubjectDataUnattachedClaim)[];
  withType?: boolean;
}) {
  /* The first-party tier reads in the org's own words. */
  const { vertical } = useOrg();

  return (
    <div className="table-scroll">
      <table className="table data">
        <thead>
          <tr>
            <th>Claim</th>
            {withType ? <th>Type</th> : null}
            <th>Source</th>
            <th>Binding</th>
            <th>Dated</th>
            <th>What it said</th>
          </tr>
        </thead>
        <tbody>
          {claims.map((claim) => (
            <tr key={claim.claim_id}>
              <td>
                <span className="mono">{shortId(claim.claim_id)}</span>
                <div className="sub">{claim.resolution_status}</div>
              </td>
              {withType ? (
                <td>{'fact_type' in claim ? FACT_TYPE_LABELS[claim.fact_type] : ''}</td>
              ) : null}
              <td>
                {claim.source_kind}
                <div className="sub">
                  {joinClauses([
                    sourceTierLabel(vertical, claim.source_tier),
                    claim.url,
                    claim.post_negation ? 'arrived after this was removed' : null,
                  ])}
                </div>
              </td>
              <td>
                {claim.binding_strength}
                <div className="sub">
                  {joinClauses([
                    claim.facet,
                    claim.run_id ? `run ${shortId(claim.run_id)}` : 'no run',
                  ])}
                </div>
              </td>
              <td>
                {claim.content_date ? formatDate(claim.content_date) : 'undated'}
                <div className="sub">
                  {joinClauses([
                    claim.content_date_basis,
                    `observed ${formatDay(claim.observed_at)}`,
                  ])}
                </div>
              </td>
              <td>
                {/* A first-party claim has no retrieval to excerpt: its evidence
                  is the prose it was read out of, named on the line below — so
                  its own verbatim sentence stands in, and the label only when
                  the claim carried no words of its own. */}
                {claim.evidence_excerpt ??
                  claim.raw ??
                  (claim.prose_input_id ? 'first-party prose' : 'no stored excerpt')}
                <div className="sub mono">
                  {claim.evidence_id
                    ? `evidence ${shortId(claim.evidence_id)}`
                    : claim.prose_input_id
                      ? `prose ${shortId(claim.prose_input_id)}`
                      : ''}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ————————————————————————— one lineage ————————————————————————— */

function LineageBlock({
  lineage,
  correctionHref,
}: {
  lineage: SubjectDataLineage;
  /**
   * Where this lineage is corrected, or null where the record has no surface
   * that corrects one. A person's is the Research tab; an organisation has no
   * research surface at all — its lookup writes the summary the Overview opens
   * with — so the line is left off rather than pointing at a page that would not
   * take the reader anywhere.
   */
  correctionHref: ((lineageId: string) => string) | null;
}) {
  const [open, setOpen] = useState(false);
  const current = lineage.versions.find((version) => version.is_current) ?? null;

  const statusLine = joinClauses([
    lineage.status,
    lineage.negation_reason ? NEGATION_LABELS[lineage.negation_reason] : null,
    lineage.sensitivity,
    // The raw-data route stamps the class the server actually computed —
    // `moderate` and `none` included. It is a metadata chip, not a treatment:
    // the honest value is the whole point of the page (§3c/§3h).
    lineage.severity,
    lineage.side === 'counterparty' ? 'held on the other side of the pair' : null,
    lineage.last_corroborated ? `last corroborated ${formatDate(lineage.last_corroborated)}` : null,
    `first recorded ${formatDay(lineage.first_recorded_at)}`,
    plural(lineage.versions.length, 'version', 'versions'),
    plural(lineage.claims.length, 'claim', 'claims'),
  ]);

  return (
    <div className={lineage.status === 'active' ? 'cat-item' : 'cat-item off'}>
      <div className="cat-line">
        <button type="button" className="src-toggle data-headline" onClick={() => setOpen(!open)}>
          {fieldsLine(current?.fields ?? null)}
        </button>
      </div>

      <div className="src">{statusLine}</div>
      <div className="src mono">
        {shortId(lineage.lineage_id)} · key {lineage.dedup_key}
      </div>

      {open ? (
        <>
          <div className="data-sub-head">Versions</div>
          <div className="table-scroll">
            <table className="table data">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Fields</th>
                  <th>Sensitivity</th>
                  <th>Written</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {lineage.versions.map((version) => (
                  <tr key={version.version_no}>
                    <td>
                      {version.version_no}
                      <div className="sub">{version.is_current ? 'current' : 'superseded'}</div>
                    </td>
                    <td>{fieldsLine(version.fields)}</td>
                    <td>
                      {version.sensitivity}
                      <div className="sub">catalog {version.catalog_version}</div>
                    </td>
                    <td>{formatDay(version.created_at)}</td>
                    <td className="mono">
                      {joinClauses([
                        version.source_claim_id
                          ? `claim ${shortId(version.source_claim_id)}`
                          : null,
                        version.created_by_decision_id
                          ? `decision ${shortId(version.created_by_decision_id)}`
                          : null,
                      ])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="data-sub-head">Claims</div>
          {lineage.claims.length > 0 ? (
            <ClaimsTable claims={lineage.claims} />
          ) : (
            <EmptyLine>No claims attached.</EmptyLine>
          )}

          {correctionHref ? (
            <div className="src">
              <IfWrites>
                <Link className="link" to={correctionHref(lineage.lineage_id)}>
                  Open on the Research tab
                </Link>
              </IfWrites>{' '}
              to correct this.
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/* ————————————————————————— the view ————————————————————————— */

/**
 * **The raw-data view, given a subject id** — the same lift `NetworkView` took,
 * and for the same reason: `GET /subjects/:id/data` has never cared what kind of
 * subject sits behind the id, and since chunk 2 the server takes an
 * organisation's. One component, one reading, so the two pages cannot drift into
 * two accounts of the same substrate.
 */
export function SubjectDataView({
  subjectId,
  correctionHref,
}: {
  subjectId: string;
  correctionHref: ((lineageId: string) => string) | null;
}) {
  const data = useSubjectData(subjectId);

  if (data.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (data.isError) return <EmptyLine>{loadErrorLine(data.error)}</EmptyLine>;

  const { lineages, unattached_claims: unattached } = data.data;

  if (lineages.length === 0 && unattached.length === 0) {
    return <EmptyLine>Nothing recorded yet.</EmptyLine>;
  }

  // Server order is fact type then first record, so a single pass groups them.
  const groups: { type: SubjectDataLineage['fact_type']; rows: SubjectDataLineage[] }[] = [];
  for (const lineage of lineages) {
    const last = groups[groups.length - 1];
    if (last && last.type === lineage.fact_type) last.rows.push(lineage);
    else groups.push({ type: lineage.fact_type, rows: [lineage] });
  }

  return (
    <>
      {unattached.length > 0 ? (
        <Section title="Claims behind no fact">
          <ClaimsTable claims={unattached} withType />
        </Section>
      ) : null}

      {groups.map((group) => (
        <div className="section cat" key={group.type}>
          <h2>{FACT_TYPE_LABELS[group.type]}</h2>
          <div className="cat-findings">
            {group.rows.map((lineage) => (
              <LineageBlock
                key={lineage.lineage_id}
                lineage={lineage}
                correctionHref={correctionHref}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/* ————————————————————————— the person's route ————————————————————————— */

/** The view over the person the record shell is holding. */
export function SubjectDataTab() {
  const { subjectId } = usePersonRecord();
  return (
    <SubjectDataView
      subjectId={subjectId}
      correctionHref={(lineageId) => `/people/${subjectId}/research#fact-${lineageId}`}
    />
  );
}
